import type { ExerciseTemplate, Trap } from '@yuan-math/exercises';
import { fraction, roundTo, toLatex, toText } from '@yuan-math/math';
import {
  choiceExercise,
  decimalTolerance,
  fractionExercise,
  fractionTrap,
  numericExercise,
  options,
  template,
  trap,
} from './authoring.ts';

/**
 * Probability rebuilt on its axioms — sample spaces, events, counting,
 * conditioning, independence and Bayes.
 *
 * Every item here is built backwards from a *rule applied outside its
 * conditions*, because that is what a wrong answer at this level almost always
 * is. Favourable ÷ total on the eleven totals of two dice; P(A) + P(B) on
 * events that overlap; unchanged probabilities on a second draw from a bag that
 * has changed. Each of those is a correct procedure carried out correctly, and
 * each is a named trap below rather than a red cross.
 *
 * Two authoring constraints recur and are worth stating once.
 *
 * **Traps collide easily here.** A probability and its odds normalise to the
 * same string whenever the complement has one outcome, and a conditional
 * computed with the wrong denominator equals the one with the right denominator
 * whenever the two totals happen to match. `distinct` below drops the
 * duplicates rather than letting whichever was listed first accuse the student
 * of an error they may not have made.
 *
 * **Decimal answers state their own precision.** `tolerance` is relative, so
 * every numeric item asking for three decimal places passes
 * `decimalTolerance(value, 3)` — a flat figure would open a band wide enough to
 * swallow the trap sitting a few thousandths away, and the diagnosis would
 * vanish along with the correction.
 */

/** n choose k, without a factorial large enough to lose precision. */
function choose(n: number, k: number): number {
  if (k < 0 || k > n) return 0;
  let result = 1;
  for (let i = 0; i < k; i += 1) result = (result * (n - i)) / (i + 1);
  return Math.round(result);
}

/**
 * Drops any trap repeating the answer or an earlier trap.
 *
 * The generator already strips a trap that grades as *correct*; this catches
 * the other collision, where two different misunderstandings produce the same
 * wrong number. Two diagnoses for one response means whichever is listed first
 * wins, and the student is told about a mistake they may not have made.
 */
const distinct = (answer: number | string, traps: Trap[]): Trap[] => {
  const seen = new Set<string>([String(answer)]);
  return traps.filter((entry) => {
    if (seen.has(entry.response)) return false;
    seen.add(entry.response);
    return true;
  });
};

/**
 * Two-way survey tables, used by the conditioning and the union items.
 *
 * Each label is written to slot into one grammatical position and one only:
 * `row` follows "are", `col` follows "they". Storing them as bare column
 * headings instead produced "29 are commutes by train and 25 works in the
 * city", which is the sort of thing that makes generated content feel
 * generated.
 */
const SURVEYS = [
  { row: 'under 30', col: 'own a bike', unit: 'people' },
  { row: 'left-handed', col: 'wear glasses', unit: 'people' },
  { row: 'studying French', col: 'also study German', unit: 'students' },
  { row: 'season-ticket holders', col: 'travel by train', unit: 'commuters' },
] as const;

export const PROBABILITY_UNIVERSITY_TEMPLATES: ExerciseTemplate[] = [
  /* ------------------------------------------------------- sample spaces */
  template({
    id: 'sample-space-size',
    conceptId: 'sample-spaces',
    description: 'Count the outcomes of a multi-stage experiment.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const coins = rng.int(1, difficulty === 3 ? 4 : 2);
      const dice = rng.int(1, difficulty === 3 ? 2 : 1);
      const total = 2 ** coins * 6 ** dice;
      const added = 2 * coins + 6 * dice;

      return numericExercise({
        prompt: `A coin is tossed ${coins} time${coins === 1 ? '' : 's'} and a fair die is rolled ${dice} time${dice === 1 ? '' : 's'}. Every outcome is recorded in order. How many outcomes does the sample space contain?`,
        value: total,
        summary: `Each toss has 2 outcomes and each roll has 6, and the stages are independent, so the counts multiply: 2^${coins} × 6^${dice} = ${total}.`,
        hint: 'Every result of the first stage can be followed by every result of the second. Does that make the counts add or multiply?',
        steps: [
          { label: 'Count the outcomes of the tosses.', latex: `2^{${coins}} = ${2 ** coins}` },
          { label: 'Count the outcomes of the rolls.', latex: `6^{${dice}} = ${6 ** dice}` },
          { label: 'Multiply, because each pairs with each.', latex: `${2 ** coins} \\times ${6 ** dice} = ${total}` },
        ],
        // Stages added rather than multiplied.
        traps: distinct(total, [trap(added, 'counting-stages-added')]),
      });
    },
  }),

  template({
    id: 'two-dice-total',
    conceptId: 'sample-spaces',
    description: 'A probability the eleven-totals sample space gets wrong.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const target = rng.int(3, 11);
      const ways = 6 - Math.abs(target - 7);

      return fractionExercise({
        prompt: `Two fair dice are rolled and their scores added. What is the probability that the total is ${target}? Give your answer as a fraction in its simplest form.`,
        value: fraction(ways, 36),
        requireSimplified: true,
        summary: `The honest sample space is the 36 ordered pairs, not the 11 possible totals. ${ways} of the 36 pairs give ${target}, so the probability is ${toText(fraction(ways, 36))}.`,
        hint: 'How many ordered pairs (first die, second die) produce that total? And how many pairs are there altogether?',
        steps: [
          { label: 'List the ordered pairs, not the totals.', latex: `|S| = 6 \\times 6 = 36` },
          { label: `Count the pairs totalling ${target}.`, latex: `${ways}` },
          { label: 'Divide.', latex: toLatex(fraction(ways, 36)) },
        ],
        traps: [
          // One of the eleven totals, treated as equally likely.
          fractionTrap(fraction(1, 11), 'outcomes-summarised-not-equally-likely'),
        ],
      });
    },
  }),

  template({
    id: 'equally-likely-judgement',
    conceptId: 'sample-spaces',
    description: 'Decide which listed sample space has equally likely outcomes.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const coins = rng.int(2, 4);
      const target = rng.int(1, coins - 1);
      /*
       * The example has to be as long as the experiment. "such as HTH" beside
       * "tossed 2 times" tells a student that a sample space may contain
       * outcomes of the wrong length, which is exactly the confusion the item
       * is about.
       */
      const example = ['H', 'T', 'H', 'T'].slice(0, coins).join('');

      return choiceExercise({
        prompt: `A fair coin is tossed ${coins} times. Which list can be used directly as a sample space of equally likely outcomes?`,
        ...options(
          [
            { label: `every ordered sequence of ${coins} results, such as ${example}`, correct: true },
            {
              /* Listed in full rather than elided: at 2 tosses "0, 1, …, 2" has an ellipsis spanning nothing. */
              label: `the ${coins + 1} possible numbers of heads: ${Array.from({ length: coins + 1 }, (_, i) => i).join(', ')}`,
              misconceptionId: 'outcomes-summarised-not-equally-likely',
            },
            {
              label: `the two results "exactly ${target} head${target === 1 ? '' : 's'}" and "not exactly ${target}"`,
              misconceptionId: 'outcomes-summarised-not-equally-likely',
            },
            { label: 'the two results "all heads" and "at least one tail"', misconceptionId: 'sample-space-undercounted' },
          ],
          rng.shuffle,
        ),
        summary: `Only the ${2 ** coins} ordered sequences are equally likely. Counting heads merges different numbers of sequences into one entry — there are ${choose(coins, target)} sequence${choose(coins, target) === 1 ? '' : 's'} with exactly ${target} head${target === 1 ? '' : 's'} and only 1 with none.`,
        hint: 'Could you bet the same amount on every entry of the list and feel fairly treated?',
      });
    },
  }),

  /* -------------------------------------------------- events and the rules */
  template({
    id: 'addition-rule-overlap',
    conceptId: 'probability-events',
    description: 'Find P(A or B) where the events overlap.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const both = rng.int(2, difficulty === 3 ? 8 : 5);
      const onlyA = rng.int(4, 14);
      const onlyB = rng.int(4, 14);
      const neither = rng.int(5, 20);
      const total = onlyA + onlyB + both + neither;
      const survey = rng.pick(SURVEYS);
      const union = onlyA + onlyB + both;

      return fractionExercise({
        prompt: `Of ${total} ${survey.unit} surveyed, ${onlyA + both} are ${survey.row} and ${onlyB + both} ${survey.col}; ${both} do both. One is chosen at random. What is the probability that they are ${survey.row} or ${survey.col}, or both? Give your answer as a fraction in its simplest form.`,
        value: fraction(union, total),
        requireSimplified: true,
        summary: `Adding ${onlyA + both} and ${onlyB + both} counts the ${both} in both groups twice, so subtract them once: ${onlyA + both} + ${onlyB + both} − ${both} = ${union}, out of ${total}.`,
        hint: 'How many have been counted twice by adding the two group sizes?',
        steps: [
          { label: 'Add the two group sizes.', latex: `${onlyA + both} + ${onlyB + both} = ${onlyA + onlyB + 2 * both}` },
          { label: 'Subtract the overlap, counted twice.', latex: `${onlyA + onlyB + 2 * both} - ${both} = ${union}` },
          { label: 'Divide by the total.', latex: toLatex(fraction(union, total)) },
        ],
        traps: distinct(toText(fraction(union, total)), [
          // The overlap left in twice.
          fractionTrap(fraction(onlyA + onlyB + 2 * both, total), 'union-overlap-double-counted'),
          // The complement: those in neither group.
          fractionTrap(fraction(neither, total), 'complement-not-subtracted'),
        ]),
      });
    },
  }),

  template({
    id: 'neither-event-complement',
    conceptId: 'probability-events',
    description: 'Find the probability that neither event happens.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const both = rng.int(3, 9);
      const onlyA = rng.int(5, 15);
      const onlyB = rng.int(5, 15);
      const neither = rng.int(4, 18);
      const total = onlyA + onlyB + both + neither;
      const union = onlyA + onlyB + both;

      return fractionExercise({
        prompt: `A group of ${total} people contains ${onlyA + both} who play chess, ${onlyB + both} who play bridge, and ${both} who play both. One is chosen at random. What is the probability they play **neither** game? Give your answer as a fraction in its simplest form.`,
        value: fraction(neither, total),
        requireSimplified: true,
        summary: `${onlyA + both} + ${onlyB + both} − ${both} = ${union} play at least one game, so ${total} − ${union} = ${neither} play neither.`,
        hint: 'Find the probability of playing at least one game first. What is left over?',
        steps: [
          { label: 'Count those playing at least one game.', latex: `${onlyA + both} + ${onlyB + both} - ${both} = ${union}` },
          { label: 'Take that from the whole group.', latex: `${total} - ${union} = ${neither}` },
          { label: 'Divide by the total.', latex: toLatex(fraction(neither, total)) },
        ],
        traps: distinct(toText(fraction(neither, total)), [
          // The union itself, where its complement was asked for.
          fractionTrap(fraction(union, total), 'complement-not-subtracted'),
          // The overlap never subtracted, so too few are left over.
          fractionTrap(fraction(total - onlyA - onlyB - 2 * both, total), 'union-overlap-double-counted'),
        ]),
      });
    },
  }),

  template({
    id: 'at-least-one-complement',
    conceptId: 'probability-events',
    description: 'Answer an "at least one" question by complement.',
    difficulties: [4, 5],
    build: ({ rng, difficulty }) => {
      const faces = rng.pick([4, 5, 6, 8, 10]);
      const rolls = rng.int(3, difficulty === 5 ? 7 : 5);
      const none = ((faces - 1) / faces) ** rolls;
      const answer = roundTo(1 - none, 3);

      return numericExercise({
        prompt: `A fair ${faces}-sided die is rolled ${rolls} times. What is the probability of getting at least one ${faces}? Give your answer to three decimal places.`,
        value: answer,
        tolerance: decimalTolerance(answer, 3),
        summary: `"At least one" has many ways to happen and "none" has one: every roll must miss, with probability (${faces - 1}/${faces})^${rolls} ≈ ${roundTo(none, 4)}. So the answer is 1 − that ≈ ${answer}.`,
        hint: 'Listing every way to get at least one is six calculations. What is the opposite of "at least one", and how many ways does that have?',
        steps: [
          { label: 'Probability one roll misses.', latex: `\\frac{${faces - 1}}{${faces}}` },
          { label: 'All the rolls miss — independent, so multiply.', latex: `\\left(\\frac{${faces - 1}}{${faces}}\\right)^{${rolls}} \\approx ${roundTo(none, 4)}` },
          { label: 'Take the complement.', latex: `1 - ${roundTo(none, 4)} \\approx ${answer}` },
        ],
        traps: distinct(answer, [
          // The complement computed and then not subtracted.
          trap(roundTo(none, 3), 'complement-not-subtracted'),
          // One roll's probability, multiplied by the number of rolls.
          trap(roundTo(rolls / faces, 3), 'union-overlap-double-counted'),
        ]),
      });
    },
  }),

  template({
    id: 'exclusive-or-independent',
    conceptId: 'probability-events',
    description: 'Distinguish mutually exclusive events from independent ones.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      /*
       * Both pairs are exclusive, and neither is a special case: a suit pair
       * and a rank pair, so a student cannot answer from the shape of the
       * question. There are no numbers in the item at all, which is the point —
       * the whole content is which of two words applies.
       */
      const pair = rng.pick([
        { a: 'the card is a heart', b: 'the card is a spade', why: 'a single card has one suit' },
        { a: 'the card is a king', b: 'the card is a queen', why: 'a single card has one rank' },
        { a: 'the card is red', b: 'the card is a club', why: 'clubs are black' },
      ]);

      return choiceExercise({
        prompt: `A single card is drawn. Event A is "${pair.a}" and event B is "${pair.b}". Which statement is true?`,
        ...options(
          [
            { label: 'A and B are mutually exclusive, so P(A and B) = 0', correct: true },
            { label: 'A and B are independent, so P(A and B) = P(A) × P(B)', misconceptionId: 'exclusive-taken-as-independent' },
            { label: 'A and B are both mutually exclusive and independent', misconceptionId: 'exclusive-taken-as-independent' },
            { label: 'P(A or B) = P(A) + P(B) − P(A and B) does not apply here', misconceptionId: 'union-overlap-double-counted' },
          ],
          rng.shuffle,
        ),
        summary: `${pair.why.charAt(0).toUpperCase()}${pair.why.slice(1)}, so the two events cannot both happen and P(A and B) = 0 — mutually exclusive. That is the opposite of independent: being told A happened tells you B definitely did not, which is as informative as an event can be. P(A) × P(B) is not zero, so the multiplication rule would give the wrong answer here.`,
        hint: 'Can both events happen on the same draw? And if one happens, what does that tell you about the other?',
      });
    },
  }),

  /* ---------------------------------------------- probability by counting */
  template({
    id: 'committee-composition',
    conceptId: 'counting-probability',
    description: 'A probability as a ratio of two combinations.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const women = rng.int(5, difficulty === 4 ? 9 : 7);
      const men = rng.int(4, difficulty === 4 ? 8 : 6);
      const size = rng.int(3, 4);
      const wanted = rng.int(1, size - 1);
      const favourable = choose(women, wanted) * choose(men, size - wanted);
      const all = choose(women + men, size);
      const answer = roundTo(favourable / all, 3);
      const added = roundTo((choose(women, wanted) + choose(men, size - wanted)) / all, 3);

      return numericExercise({
        prompt: `A committee of ${size} is chosen at random from ${women} women and ${men} men. What is the probability that it contains exactly ${wanted} ${wanted === 1 ? 'woman' : 'women'}? Give your answer to three decimal places.`,
        value: answer,
        tolerance: decimalTolerance(answer, 3),
        summary: `Choose ${wanted} of the ${women} women and ${size - wanted} of the ${men} men: ${choose(women, wanted)} × ${choose(men, size - wanted)} = ${favourable} committees, out of ${all} altogether.`,
        hint: 'Every choice of women can be paired with every choice of men. Does that make the two counts add or multiply?',
        steps: [
          { label: 'Count all the committees.', latex: `{}^{${women + men}}C_{${size}} = ${all}` },
          { label: 'Choose within each pool.', latex: `{}^{${women}}C_{${wanted}} \\times {}^{${men}}C_{${size - wanted}} = ${favourable}` },
          { label: 'Divide.', latex: `\\frac{${favourable}}{${all}} \\approx ${answer}` },
        ],
        // The two pool counts added rather than multiplied.
        traps: distinct(answer, [trap(added, 'counting-stages-added')]),
      });
    },
  }),

  template({
    id: 'at-least-one-of-a-kind',
    conceptId: 'counting-probability',
    description: 'Count the complement to answer "at least one".',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const special = rng.int(3, 6);
      const others = rng.int(10, 20);
      const total = special + others;
      const draw = rng.int(3, 5);
      const none = choose(others, draw) / choose(total, draw);
      const answer = roundTo(1 - none, 3);

      return numericExercise({
        prompt: `A box holds ${total} items, ${special} of which are faulty. ${draw} are chosen at random without replacement. What is the probability that at least one is faulty? Give your answer to three decimal places.`,
        value: answer,
        tolerance: decimalTolerance(answer, 3),
        summary: `Counting "at least one faulty" directly is ${Math.min(special, draw)} separate cases. Counting "none faulty" is one: ${choose(others, draw)} of the ${choose(total, draw)} selections avoid every faulty item, so the answer is 1 − that ≈ ${answer}.`,
        hint: 'How many selections contain no faulty item at all? That is a single combination.',
        steps: [
          { label: 'Count all the selections.', latex: `{}^{${total}}C_{${draw}} = ${choose(total, draw)}` },
          { label: 'Count those avoiding every faulty item.', latex: `{}^{${others}}C_{${draw}} = ${choose(others, draw)}` },
          { label: 'Subtract that proportion from 1.', latex: `1 - \\frac{${choose(others, draw)}}{${choose(total, draw)}} \\approx ${answer}` },
        ],
        traps: distinct(answer, [
          // The complement computed and reported.
          trap(roundTo(none, 3), 'complement-not-subtracted'),
          // One draw's chance, scaled by the number drawn.
          trap(roundTo((draw * special) / total, 3), 'independence-assumed-without-replacement'),
        ]),
      });
    },
  }),

  template({
    id: 'order-matters-choice',
    conceptId: 'counting-probability',
    description: 'Choose between a permutation and a combination.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const n = rng.int(7, 12);
      const k = rng.int(3, 4);
      const ordered = rng.bool();
      const story = ordered
        ? `${n} runners finish a race; the first ${k} take gold, silver and bronze`
        : `${n} students are chosen from, to form a team of ${k} with no captain`;

      return choiceExercise({
        prompt: `${story}. How many different results are possible?`,
        ...options(
          [
            ordered
              ? { latex: `{}^{${n}}P_{${k}} = ${choose(n, k) * factorial(k)}`, correct: true }
              : { latex: `{}^{${n}}C_{${k}} = ${choose(n, k)}`, correct: true },
            ordered
              ? { latex: `{}^{${n}}C_{${k}} = ${choose(n, k)}`, misconceptionId: 'order-ignored-when-it-matters' }
              : { latex: `{}^{${n}}P_{${k}} = ${choose(n, k) * factorial(k)}`, misconceptionId: 'order-counted-when-it-does-not-matter' },
            { latex: `${n}^{${k}} = ${n ** k}`, misconceptionId: 'replacement-assumed' },
            { latex: `${n} \\times ${k} = ${n * k}`, misconceptionId: 'counting-stages-added' },
          ],
          rng.shuffle,
        ),
        summary: ordered
          ? `Gold, silver and bronze are three different jobs, so the same ${k} runners in another order is a different result — a permutation.`
          : `A team with no captain is just a set of ${k} people, so reordering it changes nothing — a combination.`,
        hint: 'If you swapped two of the chosen people around, would the result be different?',
      });
    },
  }),

  /* --------------------------------------------- conditional probability */
  template({
    id: 'conditional-from-table',
    conceptId: 'conditional-probability',
    description: 'Read a conditional probability off a two-way table.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const both = rng.int(8, difficulty === 3 ? 30 : 20);
      const rowOnly = rng.int(6, 25);
      const colOnly = rng.int(6, 25);
      const neither = rng.int(10, 40);
      const rowTotal = both + rowOnly;
      const colTotal = both + colOnly;
      const total = rowTotal + colOnly + neither;
      const survey = rng.pick(SURVEYS);

      return fractionExercise({
        prompt: `Of ${total} ${survey.unit}, ${rowTotal} are ${survey.row} and ${colTotal} ${survey.col}; ${both} do both. One is chosen at random. Given that they are ${survey.row}, what is the probability that they ${survey.col}? Give your answer as a fraction in its simplest form.`,
        value: fraction(both, rowTotal),
        requireSimplified: true,
        summary: `Being told they are ${survey.row} shrinks the sample space to those ${rowTotal}, and ${both} of them ${survey.col}: ${toText(fraction(both, rowTotal))}.`,
        hint: 'The condition tells you which group you are now inside. That group is what goes underneath.',
        steps: [
          { label: 'The condition becomes the denominator.', latex: `${rowTotal}` },
          { label: 'Count those in the group who also qualify.', latex: `${both}` },
          { label: 'Divide.', latex: toLatex(fraction(both, rowTotal)) },
        ],
        traps: distinct(toText(fraction(both, rowTotal)), [
          // The grand total used, which answers P(A and B) instead.
          fractionTrap(fraction(both, total), 'conditional-denominator-is-total'),
          // Conditioned the other way round.
          fractionTrap(fraction(both, colTotal), 'conditional-direction-reversed'),
        ]),
      });
    },
  }),

  template({
    id: 'two-draws-no-replacement',
    conceptId: 'conditional-probability',
    description: 'Multiply along a tree where the bag has changed.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const red = rng.int(4, 9);
      const blue = rng.int(3, 8);
      const total = red + blue;
      const answer = fraction(red * (red - 1), total * (total - 1));

      return fractionExercise({
        prompt: `A bag holds ${red} red and ${blue} blue counters. Two are drawn without replacement. What is the probability that both are red? Give your answer as a fraction in its simplest form.`,
        value: answer,
        requireSimplified: true,
        summary: `P(first red) = ${red}/${total}. After a red has gone, ${red - 1} reds remain among ${total - 1} counters, so P(second red | first red) = ${red - 1}/${total - 1}. Multiply along the path: ${toText(answer)}.`,
        hint: 'What is in the bag for the second draw? It is not what was in it for the first.',
        steps: [
          { label: 'First draw.', latex: `\\frac{${red}}{${total}}` },
          { label: 'Second draw, given the first was red.', latex: `\\frac{${red - 1}}{${total - 1}}` },
          { label: 'Multiply along the branch.', latex: toLatex(answer) },
        ],
        traps: distinct(toText(answer), [
          // The bag treated as though the first counter went back.
          fractionTrap(fraction(red * red, total * total), 'independence-assumed-without-replacement'),
          // The two branch probabilities added instead of multiplied.
          fractionTrap(fraction(red * (total - 1) + (red - 1) * total, total * (total - 1)), 'compound-probability-added'),
        ]),
      });
    },
  }),

  template({
    id: 'conditional-second-draw',
    conceptId: 'conditional-probability',
    description: 'A conditional probability read straight off the second stage.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const red = rng.int(4, 10);
      const blue = rng.int(4, 10);
      const total = red + blue;

      return fractionExercise({
        prompt: `A bag holds ${red} red and ${blue} blue counters. Two are drawn without replacement, and the first is red. What is the probability that the second is blue? Give your answer as a fraction in its simplest form.`,
        value: fraction(blue, total - 1),
        requireSimplified: true,
        summary: `One red has gone, so ${total - 1} counters remain and all ${blue} blues are still there: ${toText(fraction(blue, total - 1))}.`,
        hint: 'How many counters are left, and how many of them are blue?',
        steps: [
          { label: 'Count what remains after a red is removed.', latex: `${total} - 1 = ${total - 1}` },
          { label: 'The blues are untouched.', latex: `${blue}` },
          { label: 'Divide.', latex: toLatex(fraction(blue, total - 1)) },
        ],
        traps: distinct(toText(fraction(blue, total - 1)), [
          // The original bag, as though the counter had been replaced.
          fractionTrap(fraction(blue, total), 'independence-assumed-without-replacement'),
          // Both draws' probability given, where only the second was asked for.
          fractionTrap(fraction(red * blue, total * (total - 1)), 'conditional-denominator-is-total'),
        ]),
      });
    },
  }),

  /* ---------------------------------------------------------- independence */
  template({
    id: 'independence-multiplication-test',
    conceptId: 'independence',
    description: 'Test two events for independence from a table.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const scale = rng.int(2, 5);
      const independent = rng.bool();
      /* An independent table is a product table: cell = row × column ÷ total. */
      const a = 3 * scale;
      const b = 2 * scale;
      const c = 6 * scale;
      const d = independent ? 4 * scale : 4 * scale + rng.int(2, 4) * scale;
      const total = a + b + c + d;
      const rowTotal = a + b;
      const colTotal = a + c;

      return choiceExercise({
        prompt: `In a survey of ${total} people, ${rowTotal} are left-handed, ${colTotal} wear glasses, and ${a} are both. Are "left-handed" and "wears glasses" independent?`,
        ...options(
          [
            independent
              ? { label: 'Yes — P(both) equals P(left-handed) × P(glasses)', correct: true }
              : { label: 'No — P(both) differs from P(left-handed) × P(glasses)', correct: true },
            independent
              ? { label: 'No — the two groups overlap, so one affects the other', misconceptionId: 'exclusive-taken-as-independent' }
              : { label: 'Yes — nothing in the story connects handedness to eyesight', misconceptionId: 'exclusive-taken-as-independent' },
            { label: 'They cannot be independent, because they are not mutually exclusive', misconceptionId: 'exclusive-taken-as-independent' },
            {
              label: 'It depends which way round you condition — P(glasses | left-handed) and P(left-handed | glasses) disagree',
              misconceptionId: 'conditional-direction-reversed',
            },
          ],
          rng.shuffle,
        ),
        summary: `Independence is decided by arithmetic, not by the story: P(left) × P(glasses) = ${roundTo(rowTotal / total, 4)} × ${roundTo(colTotal / total, 4)} = ${roundTo((rowTotal * colTotal) / (total * total), 4)}, against an actual P(both) of ${roundTo(a / total, 4)}. ${independent ? 'They agree, so the events are independent.' : 'They disagree, so the events are not independent.'}`,
        hint: 'Compute P(A) × P(B) and compare it with the P(A and B) the table actually reports.',
      });
    },
  }),

  template({
    id: 'independent-survival',
    conceptId: 'independence',
    description: 'Multiply independent probabilities across repeated trials.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const failPercent = rng.pick([2, 3, 4, 5, 6, 8, 10]);
      const days = rng.int(3, difficulty === 3 ? 8 : 5);
      const q = 1 - failPercent / 100;
      const answer = roundTo(q ** days, 3);

      return numericExercise({
        prompt: `A component fails on any given day with probability ${failPercent / 100}, independently of other days. What is the probability that it survives ${days} days? Give your answer to three decimal places.`,
        value: answer,
        tolerance: decimalTolerance(answer, 3),
        summary: `It must survive every day, and the days are independent, so the survival probabilities multiply: ${q}^${days} ≈ ${answer}.`,
        hint: 'Surviving the week means surviving day one *and* day two *and* … — what does independence let you do with an "and"?',
        steps: [
          { label: 'Probability of surviving one day.', latex: `1 - ${failPercent / 100} = ${q}` },
          { label: 'Independent days multiply.', latex: `${q}^{${days}} \\approx ${answer}` },
        ],
        traps: distinct(answer, [
          // The failure probability scaled by the number of days.
          trap(roundTo((failPercent / 100) * days, 3), 'compound-probability-added'),
          // The chance of at least one failure — the complement.
          trap(roundTo(1 - q ** days, 3), 'complement-not-subtracted'),
        ]),
      });
    },
  }),

  template({
    id: 'independence-with-and-without-replacement',
    conceptId: 'independence',
    description: 'Decide whether repeated draws are independent.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const red = rng.int(4, 9);
      const blue = rng.int(3, 8);
      const total = red + blue;
      const withReplacement = rng.bool();

      return choiceExercise({
        prompt: `A bag holds ${red} red and ${blue} blue counters. Two are drawn ${withReplacement ? 'with' : 'without'} replacement. What is P(second is red | first is red)?`,
        ...options(
          [
            withReplacement
              ? { latex: `\\frac{${red}}{${total}}`, correct: true }
              : { latex: `\\frac{${red - 1}}{${total - 1}}`, correct: true },
            withReplacement
              ? { latex: `\\frac{${red - 1}}{${total - 1}}`, misconceptionId: 'independence-assumed-without-replacement' }
              : { latex: `\\frac{${red}}{${total}}`, misconceptionId: 'independence-assumed-without-replacement' },
            { latex: `\\frac{${red}}{${total}} \\times \\frac{${red - 1}}{${total - 1}}`, misconceptionId: 'conditional-denominator-is-total' },
            { latex: `\\frac{${blue}}{${total - 1}}`, misconceptionId: 'complement-not-subtracted' },
          ],
          rng.shuffle,
        ),
        summary: withReplacement
          ? `The counter went back, so the bag for the second draw is the bag for the first. The draws are independent and the condition changes nothing: ${red}/${total}.`
          : `A red has gone, so ${red - 1} reds remain among ${total - 1} counters. The draws are dependent, and the condition is exactly what changes the answer.`,
        hint: 'What is in the bag at the moment of the second draw?',
      });
    },
  }),

  /* -------------------------------------------------------- Bayes' theorem */
  template({
    id: 'bayes-two-suppliers',
    conceptId: 'bayes-theorem',
    description: 'Reverse a conditional probability across two suppliers.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      /*
       * Percentages are kept as whole numbers and divided only where a
       * probability is needed. Storing `x / 100` and multiplying back by 100 to
       * print it produced "7.000000000000001% are faulty" — invisible in the
       * arithmetic and instantly visible on the page.
       */
      const sharePercent = rng.pick([40, 50, 60, 70, 75]);
      const faultAPercent = rng.pick([2, 3, 4, 5]);
      const faultBPercent = rng.pick([6, 7, 8, 10]);
      const shareA = sharePercent / 100;
      const shareB = roundTo(1 - shareA, 2);
      const faultA = faultAPercent / 100;
      const faultB = faultBPercent / 100;
      const pathA = shareA * faultA;
      const pathB = shareB * faultB;
      const answer = roundTo(pathA / (pathA + pathB), 3);

      return numericExercise({
        prompt: `Factory A makes ${sharePercent}% of a shop's items and ${faultAPercent}% of them are faulty; factory B makes the rest, of which ${faultBPercent}% are faulty. An item is found to be faulty. What is the probability it came from factory A? Give your answer to three decimal places.`,
        value: answer,
        tolerance: decimalTolerance(answer, 3),
        summary: `Multiply along each path to a fault: A gives ${roundTo(pathA, 4)} and B gives ${roundTo(pathB, 4)}. The denominator is every path that ends in a fault, so the answer is ${roundTo(pathA, 4)} ÷ ${roundTo(pathA + pathB, 4)} ≈ ${answer}.`,
        hint: 'Draw the tree. Which paths end with a faulty item, and which of them is the one you want?',
        steps: [
          { label: 'The path through A.', latex: `${shareA} \\times ${faultA} = ${roundTo(pathA, 4)}` },
          { label: 'The path through B.', latex: `${shareB} \\times ${faultB} = ${roundTo(pathB, 4)}` },
          { label: 'Total probability of a fault.', latex: `${roundTo(pathA, 4)} + ${roundTo(pathB, 4)} = ${roundTo(pathA + pathB, 4)}` },
          { label: 'Divide the path you want by the total.', latex: `\\frac{${roundTo(pathA, 4)}}{${roundTo(pathA + pathB, 4)}} \\approx ${answer}` },
        ],
        traps: distinct(answer, [
          // The joint probability, never divided by the total.
          trap(roundTo(pathA, 3), 'bayes-denominator-single-path'),
          // The fault rate of factory A, quoted as though it were the answer.
          trap(roundTo(faultA, 3), 'base-rate-ignored'),
          // The prior, unrevised by the evidence.
          trap(roundTo(shareA, 3), 'base-rate-ignored'),
        ]),
      });
    },
  }),

  template({
    id: 'bayes-screening-test',
    conceptId: 'bayes-theorem',
    description: 'A rare condition, an accurate test, and a surprising posterior.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const perThousand = rng.pick([1, 2, 4, 5]);
      const prevalence = perThousand / 1000;
      const sensitivityPercent = rng.pick([90, 95, 98, 99]);
      const falsePositivePercent = rng.pick([1, 2, 3, 5]);
      const sensitivity = sensitivityPercent / 100;
      const falsePositive = falsePositivePercent / 100;
      const truePath = prevalence * sensitivity;
      const falsePath = (1 - prevalence) * falsePositive;
      const answer = roundTo(truePath / (truePath + falsePath), 3);

      return numericExercise({
        prompt: `A condition affects ${perThousand} in every 1000 people. A test detects it in ${sensitivityPercent}% of those who have it, and wrongly flags ${falsePositivePercent}% of those who do not. Someone tests positive. What is the probability they have the condition? Give your answer to three decimal places.`,
        value: answer,
        tolerance: decimalTolerance(answer, 3),
        summary: `Out of 100,000 people, about ${Math.round(truePath * 100000)} have it and test positive, while about ${Math.round(falsePath * 100000)} do not have it and test positive anyway. Only ${Math.round(truePath * 100000)} of those ${Math.round((truePath + falsePath) * 100000)} positives are genuine: ≈ ${answer}.`,
        hint: 'The false-positive rate is small, but it is applied to almost everybody. How many healthy people does it flag?',
        steps: [
          { label: 'True positives.', latex: `${prevalence} \\times ${sensitivity} = ${roundTo(truePath, 5)}` },
          { label: 'False positives.', latex: `${roundTo(1 - prevalence, 4)} \\times ${falsePositive} = ${roundTo(falsePath, 5)}` },
          { label: 'All positives.', latex: `${roundTo(truePath + falsePath, 5)}` },
          { label: 'Divide.', latex: `\\frac{${roundTo(truePath, 5)}}{${roundTo(truePath + falsePath, 5)}} \\approx ${answer}` },
        ],
        traps: distinct(answer, [
          // The detection rate, read as the answer — the base-rate fallacy.
          trap(roundTo(sensitivity, 3), 'base-rate-ignored'),
          // The joint probability, undivided.
          trap(roundTo(truePath, 3), 'bayes-denominator-single-path'),
          // The prevalence, unrevised.
          trap(roundTo(prevalence, 3), 'base-rate-ignored'),
        ]),
      });
    },
  }),

  template({
    id: 'total-probability-denominator',
    conceptId: 'bayes-theorem',
    description: 'Assemble the denominator with the law of total probability.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const sharePercent = rng.pick([30, 40, 55, 60, 65, 80]);
      const rateAPercent = rng.pick([2, 4, 5, 6]);
      const rateBPercent = rng.pick([8, 10, 12, 15]);
      const shareA = sharePercent / 100;
      const shareB = roundTo(1 - shareA, 2);
      const rateA = rateAPercent / 100;
      const rateB = rateBPercent / 100;
      const answer = roundTo(shareA * rateA + shareB * rateB, 4);

      return numericExercise({
        prompt: `Machine A makes ${sharePercent}% of the output and rejects ${rateAPercent}% of what it makes; machine B makes the rest and rejects ${rateBPercent}%. What proportion of the whole output is rejected? Give your answer to three decimal places.`,
        value: roundTo(answer, 3),
        tolerance: decimalTolerance(roundTo(answer, 3), 3),
        summary: `Rejects arrive along two paths: ${shareA} × ${rateA} = ${roundTo(shareA * rateA, 4)} and ${shareB} × ${rateB} = ${roundTo(shareB * rateB, 4)}. Add the paths, not the rates: ≈ ${roundTo(answer, 3)}.`,
        hint: 'Weight each machine’s reject rate by how much of the output it makes.',
        steps: [
          { label: 'The path through A.', latex: `${shareA} \\times ${rateA} = ${roundTo(shareA * rateA, 4)}` },
          { label: 'The path through B.', latex: `${shareB} \\times ${rateB} = ${roundTo(shareB * rateB, 4)}` },
          { label: 'Add the paths.', latex: `${roundTo(answer, 3)}` },
        ],
        traps: distinct(roundTo(answer, 3), [
          // The two conditional rates added, without weighting.
          trap(roundTo(rateA + rateB, 3), 'bayes-denominator-single-path'),
          // The unweighted average of the two rates.
          trap(roundTo((rateA + rateB) / 2, 3), 'expected-value-unweighted'),
        ]),
      });
    },
  }),
];

/** k! — small k only, which is all the permutation items need. */
function factorial(k: number): number {
  let result = 1;
  for (let i = 2; i <= k; i += 1) result *= i;
  return result;
}
